import { FeatureFlagIds } from "../utils/featureFlagIds";
import CardHeader from "@mui/material/CardHeader";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import {
  canUnderwrite,
  canViewQuote,
  isAnyRequiredChecklistIncomplete
} from "../utils/appUtils";
import Button from "@mui/material/Button";
import PrintableQuote from "./Views/OrderEditor/PrintableQuote/PrintableQuote";
import {calculatePayment, isInternalDocumentsIndirectQuoterReadOnly} from "../utils/quoteUtils";
import Big from "big.js";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import Card from "@mui/material/Card";
import {useSelector} from "react-redux";
import {IConfiguration, useConfiguration} from "../Data/ConfigurationData";
import {allIndirectSubmissionDecisionCodes, Constants, indirectStipulationStatuses} from "../utils/constants";
import {Lookup} from "../Interfaces/StipulationTypes";
import { useApplicationCustomerStipulations } from "../Data/StipulationData";
import PrintComponents from "react-print-components";
import {IndirectDecisionBoxUnderwriterQuotes} from "./Views/OrderEditor/IndirectDecisionBoxUnderwriterQuotes";
import {IApplication} from "../Interfaces/Application/IApplication";
import { httpPostAsync } from "../utils/HttpClient";
import { saveFile } from "../utils/utils";
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import { AppState } from "../store/rootReducer";
import { authService } from "..";
import { getAppQuoteByType} from "./Quoters/IndirectQuoter/IndirectQuoterUtils";
import {IndirectQuoteType} from "../Data/IndirectQuoteType";
import { ApplicationQuoteType } from "@amsanalytics/react-shared-library";
import { StatusWorkflowComponent } from "./Views/OrderEditor/StatusWorkflowComponent";
import { includes } from "ramda";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { IconButton } from "@mui/material";

interface DecisionBoxIndirectUnderwriterViewProps {
  updateApplication: (fn: (app: IApplication) => IApplication | void) => void;
  handleSaveAppFromOverview: (currentApp: IApplication, originalApplicationStatus: string, bypassAppRequirements: boolean) => Promise<void>;
  configuration: IConfiguration;
}

export default function DecisionBoxIndirectUnderwriterView(props: DecisionBoxIndirectUnderwriterViewProps) {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const indirectStipulations = useSelector((state: AppState) => state.dealerService.applicationStipulations);
  const { data: stipulationData } = useApplicationCustomerStipulations(currentApp ? currentApp.id : Constants.emptyGuid);
  const underwriterQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Underwriter)?.quote;
  const { data: configuration } = useConfiguration();
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const dealerRequestedQuote = getAppQuoteByType(currentApp, IndirectQuoteType.DealerRequested);
  const decisionHistory = useSelector((state: AppState) => state.applications.dealerServiceAppHistoryItems);
  
  const areIndirectStipulationsMet = (): boolean => {

    // If indirectStipulations is undefined or empty, consider condition met
    if (!indirectStipulations || indirectStipulations.length === 0) {
      return true;
    }     
    // Check if all stipulations have the 'passed' status
    return indirectStipulations.every(stipulation => 
      (stipulation.statusId?.toUpperCase() === indirectStipulationStatuses.passed || stipulation.statusId === indirectStipulationStatuses.waived));
  };

  const stipulationCategories: Lookup[] = [];
  stipulationData?.stipulationCategoryTypes.forEach(st => st.stipulationCategories.forEach((sc, index) => {
    stipulationCategories.push({
      id: sc.id,
      name: sc.name,
      description: sc.description,
      sortOrder: sc.sortOrder
    });
  }));

    const preventDeskingDueToIncompleteChecklist = currentApp &&
      props.configuration?.requiredChecklistsForDesking &&
      props.configuration.requiredChecklistsForDesking.length > 0
      ? isAnyRequiredChecklistIncomplete(props.configuration.requiredChecklistsForDesking, currentApp?.applicationChecklists)
      : false;
  
    const preventDeskingDueToIncompleteStips = props.configuration.featureFlags?.[FeatureFlagIds.DisableDeskingIfRequiredStipsIncomplete] === true
      && (areIndirectStipulationsMet() === false);
  
  const preventDeskingOrPosting = preventDeskingDueToIncompleteChecklist || preventDeskingDueToIncompleteStips;

  const indirectQuoterIsReadOnly = isInternalDocumentsIndirectQuoterReadOnly(currentApp, props.configuration);

  const viewQuotePresentationReport = async () => {
    const bearerToken = await authService.getToken();

    const report = await httpPostAsync(`api/reporting/getReport`,
      {
        reportName: configuration!.quotePresentationReport!,
        reportParameters: [{
          name: "AppNumber",
          value: currentApp!.applicationNo,
        },
        ],
        reportFormat: "1"
      }, bearerToken!);

    saveFile(report!, 'report.pdf');
  }

  const viewDecisionReport = async (application: IApplication, decisionCode: string) => {
    const bearerToken = await authService.getToken();

    const report = await httpPostAsync(`api/reporting/getReport`,
      {
        reportName: includes(decisionCode, ['A', 'PA'])
          ? configuration!.decisionReportApproved
          : includes(decisionCode, ['CO', 'O']) // A, CO, D, DC, O, PA
            ? configuration!.decisionReportConditional
            : configuration!.decisionReportDeclined, // Use the appropriate report based on the status
        reportParameters: [{
          name: "AppNumber",
          value: currentApp!.applicationNo,
        }],
        reportFormat: "1"
      }, bearerToken!);

    saveFile(report!, 'decisionReport.pdf');
  }

  return(
    <Card style={{ marginTop: 8 }}>
      <CardHeader
        style={{display: 'flex', alignItems: 'center', overflow: 'auto'}}
        avatar={<AssignmentTurnedInIcon fontSize="small" />}
        title={<span style={{whiteSpace: 'none', textWrap: 'nowrap', marginRight: '10px'}}>Decision Box</span>}
        action={
          <div style={{display: 'flex', flex: '1', flexDirection: 'row', width: '100%', alignItems: 'center', overflow: 'auto'}}>
            {(currentApp?.quoteType === ApplicationQuoteType.Indirect) && dealerRequestedQuote && configuration              
              && (
                <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', overflow: 'auto' }}>
                  <div style={{display: 'flex'}}>
                    {configuration.quotePresentationReport &&
                      <Button
                        style={{ marginRight: "10px", textTransform: 'none' }}
                        variant="contained"
                        onClick={() => viewQuotePresentationReport()}>
                        <LocalPrintshopIcon fontSize="small" style={{ marginRight: '3px' }} />
                        Quote
                      </Button>
                    }                  
                  </div>
                  <div style={{display: 'flex', flex: 1, alignItems:'center', overflow: 'auto'}}>
                    {decisionHistory.decisionsSent?.some(d => includes(d.decisionCode, allIndirectSubmissionDecisionCodes) === true) 
                      && decisionHistory.decisionsSent.find(d => d.id === decisionHistory.currentDecisionId)?.decisionCode !== undefined
                      && // A, CO, D, DC, O, PA
                      <Tooltip anchorElement="target" position="top" parentTitle={true}>
                        <IconButton
                          title={'Print Decision'}
                          style={{color: '#757575'}}
                          onClick={() => {
                            const decisionCode = decisionHistory.decisionsSent.find(d => d.id == decisionHistory.currentDecisionId)?.decisionCode;
                            if (!currentApp || !decisionCode) return;
                            viewDecisionReport(currentApp, decisionCode);
                          }}
                        >
                          <LocalPrintshopIcon style={{fontSize: '30px'}} />
                        </IconButton>
                      </Tooltip>
                    }   
                    <StatusWorkflowComponent style={{overflow: 'auto', maxWidth: '100%'}} 
                      disableDeskingOrPosting={preventDeskingOrPosting}
                    />
                  </div>
                </div>
              )}
                {props.configuration?.featureFlags?.[FeatureFlagIds.HidePrintButtonIndirectDecisionBox] !== true &&
                    currentApp &&
                    underwriterQuote &&
                    canViewQuote(currentApp) && (
                        <PrintComponents trigger={<Button variant="contained" onClick={(e) => { }} color="inherit">Print</Button>}>
                            <PrintableQuote
                                app={currentApp}
                                paymentInfo={{
                                    monthlyPayment: calculatePayment(underwriterQuote.apr ?? 0, 12, (underwriterQuote.term ?? 0) + (underwriterQuote.balloonTerm ?? 0), underwriterQuote.amountToFinance ?? 0), // Use 12 for paymentsPerYear to calculate monthly
                                    numberOfPayments: Number.parseFloat(new Big(underwriterQuote.term ?? 0).mul(new Big(underwriterQuote.quotePayType?.paymentsPerYear ?? 12).div(12)).toFixed(2)),
                                    payment: calculatePayment(underwriterQuote.apr ?? 0, underwriterQuote.quotePayType?.paymentsPerYear ?? 12, (underwriterQuote.term ?? 0) + (underwriterQuote.balloonTerm ?? 0), underwriterQuote.amountToFinance ?? 0),
                                }}
                                quote={underwriterQuote}
                                PriceDataOnly={false}
                                configuration={configuration}
                            />
                        </PrintComponents>
                    )
                }
          </div>
      }
    />     
    <Divider variant="middle" />
    <CardContent>
      {!canUnderwrite(activeUser)
        ? (currentApp && underwriterQuote && canViewQuote(currentApp)
          ?
            <PrintableQuote 
              app={currentApp}
              paymentInfo={{
                monthlyPayment: !underwriterQuote ? 0 : calculatePayment(underwriterQuote.apr ?? 0, 12, (underwriterQuote.term ?? 0) + (underwriterQuote.balloonTerm ?? 0), underwriterQuote.amountToFinance ?? 0), // Use 12 for paymentsPerYear to calculate monthly
                numberOfPayments: !underwriterQuote ? 0 : Number.parseFloat(new Big(underwriterQuote.term ?? 0).mul(new Big(underwriterQuote.quotePayType?.paymentsPerYear ?? 12).div(12)).toFixed(2)),
                payment: !underwriterQuote ? 0 : calculatePayment(underwriterQuote.apr ?? 0, underwriterQuote.quotePayType?.paymentsPerYear ?? 12, (underwriterQuote.term ?? 0) + (underwriterQuote.balloonTerm ?? 0), underwriterQuote.amountToFinance ?? 0),
              }}
              quote={underwriterQuote} PriceDataOnly={true}
              configuration={configuration}
            />
          : <div></div>
          )
        : 
        <>
          <IndirectDecisionBoxUnderwriterQuotes
            {...props}
            configuration={props.configuration}
            readOnly={indirectQuoterIsReadOnly}
          />
        </>
      }
    </CardContent>
  </Card>
  );
};